import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import filter from 'lodash/fp/filter';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { productsCategories, productsSlugs } from 'constants/products';

import LoadingScene from 'Scenes/LoadingScene';

import { haircareProductsGridContent, skincareProductsGridContent } from 'assets/content/homeV2';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { useFetchProductsList } from 'dux/products/hooks';

import { DesktopProductsGrid } from './DesktopProductsGrid';
import { MobileProductsGrid } from './MobileProductsGrid';
import { ProductCard } from './ProductCard';
import { TabletProductsGrid } from './TabletProductsGrid';

const toFilterSlugs = [
  productsSlugs.SHAMPOO,
  productsSlugs.CONDITIONER,
  productsSlugs.SCALP_SERUM,
  productsSlugs.HAIR_MASK,
  productsSlugs.CURL_CREAM,
  productsSlugs.SUPPLEMENT_CORE,
];

/** value comes from a hook returning differents kind
 * of objects we should migrate this selector to ts so the value
 * can be correctly type
 */
// @ts-expect-error migrate useFetchProductsList to ts
const filterAndOrderProducts = (products, slugsToFilter: typeof toFilterSlugs) =>
  filter((product) => slugsToFilter.includes(product.slug), products).sort(
    (a, b) => slugsToFilter.indexOf(a.slug) - toFilterSlugs.indexOf(b.slug),
  );

const SectionContainer = styled.div`
  width: 100%;
  margin: ${theme.spacing['8x']} auto;
  ${legacyTheme.breakpoints.up('sm')} {
    margin: 0;
  }
  ${legacyTheme.breakpoints.up('md')} {
    margin: 0;
  }
`;

type ProductsGridProps = {
  category: string;
  setIsOpen: () => void;
  dataTestId?: string;
};

export const ProductsGrid = ({ category, setIsOpen, dataTestId }: ProductsGridProps) => {
  const { isLoaded, haircareProducts, skincareProducts } = useFetchProductsList();
  const { isMobile, isDesktop, isTablet } = useResponsiveVariant();

  const filteredProductHaircare = filterAndOrderProducts(haircareProducts, toFilterSlugs);
  const textData =
    category === productsCategories.HAIRCARE
      ? haircareProductsGridContent
      : skincareProductsGridContent;
  if (!isLoaded) {
    // @ts-expect-error LoadingScene not migrated yet
    return <LoadingScene />;
  }

  const productList =
    category === productsCategories.HAIRCARE ? filteredProductHaircare : skincareProducts;
  const cards = productList.map((product) => {
    return (
      <ProductCard
        key={product.label}
        dataClick={`${product.slug}-card`}
        dataFrom={`${category}-grid}`}
        product={product}
      />
    );
  });

  return (
    <SectionContainer data-testid={dataTestId}>
      {isMobile && (
        <MobileProductsGrid
          fetchedProducts={productList}
          setIsOpen={setIsOpen}
          textData={textData}
        />
      )}
      {isDesktop && <DesktopProductsGrid cards={cards} textData={textData} />}
      {isTablet && <TabletProductsGrid cards={cards} setIsOpen={setIsOpen} textData={textData} />}
    </SectionContainer>
  );
};
